import { useCallback } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, firestorePathId } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { getLastUserAction, getSystemLogSessionId, logErrorToService } from '../utils/logService';

const PORTAL_ID = 'portal-servidor';
const PORTAL_ORIGIN = 'portal-servidor';
const SYSTEM_VERSION = '1.0.0';

const SENSITIVE_KEYWORDS = [
  'senha',
  'password',
  'token',
  'accessToken',
  'refreshToken',
  'idToken',
  'base64',
  'arquivoBase64',
  'conteudoBase64',
  'imagemBase64',
  'pdfBase64',
  'cpf',
  'doc_cpf',
  'servidorCpf',
  'userCpf',
  'rg',
  'telefone',
  'fone',
  'endereco',
  'enderecoCompleto',
  'dadosBancarios',
  'banco',
  'agencia',
  'conta',
];

const ACTION_TYPE_BY_PREFIX = {
  LOGIN: 'autenticacao',
  LOGOUT: 'autenticacao',
  PAGE: 'consulta',
  VIEW: 'consulta',
  OPEN: 'consulta',
  DOWNLOAD: 'download',
  CREATE: 'criacao',
  SEND: 'envio',
  SUBMIT: 'envio',
  UPLOAD: 'envio',
  UPDATE: 'alteracao',
  CHANGE: 'alteracao',
  ERROR: 'erro',
};

const normalizeKey = (key) => String(key || '').toLowerCase();

const shouldMaskKey = (key) => {
  const normalized = normalizeKey(key);
  return SENSITIVE_KEYWORDS.some((sensitiveKey) => normalized.includes(String(sensitiveKey).toLowerCase()));
};

const maskCpfLikeValue = (value) => {
  const text = String(value || '');
  const digits = text.replace(/\D/g, '');

  if (digits.length !== 11) return text;

  return `***.${digits.substring(3, 6)}.${digits.substring(6, 9)}-**`;
};

const sanitizeValue = (value, key = '', depth = 0) => {
  if (shouldMaskKey(key)) {
    if (normalizeKey(key).includes('cpf')) return maskCpfLikeValue(value);
    return '[DADO_PROTEGIDO]';
  }

  if (value === null || value === undefined) return value;

  if (typeof value === 'string') {
    const digits = value.replace(/\D/g, '');
    if (digits.length === 11) return maskCpfLikeValue(value);
    if (value.length > 600) return `${value.substring(0, 600)}...`;
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') return value;

  if (value instanceof Date) return value.toISOString();

  if (depth >= 3) return '[OBJETO_PROFUNDO]';

  if (Array.isArray(value)) {
    return value.slice(0, 30).map((item) => sanitizeValue(item, key, depth + 1));
  }

  if (typeof value === 'object') {
    return Object.entries(value).reduce((acc, [childKey, childValue]) => {
      acc[childKey] = sanitizeValue(childValue, childKey, depth + 1);
      return acc;
    }, {});
  }

  return String(value);
};

const safeJsonStringify = (value) => {
  try {
    return JSON.stringify(value || {}, null, 2);
  } catch (error) {
    return JSON.stringify({ erro: 'Nao foi possivel serializar os detalhes do registro.' });
  }
};

const isAlreadyPersistedAudit = (error) => {
  const code = String(error?.code || '').toLowerCase();
  const message = String(error?.message || '').toLowerCase();
  return code.includes('already-exists') || message.includes('document already exists');
};

const getSessionUser = () => {
  if (typeof sessionStorage === 'undefined') return null;

  try {
    const sessionData = JSON.parse(sessionStorage.getItem('servidorSessionData') || '{}');
    return sessionData?.user || null;
  } catch (error) {
    return null;
  }
};

const getSessionVinculo = () => {
  if (typeof sessionStorage === 'undefined') return null;

  try {
    const sessionData = JSON.parse(sessionStorage.getItem('servidorSessionData') || '{}');
    return sessionData?.vinculoSelecionado || null;
  } catch (error) {
    return null;
  }
};

const normalizeLogParams = (param2 = null, param3 = null, param4 = {}) => {
  let details = {};
  let entityId = 'N/A';
  let entityType = 'SISTEMA';

  if (typeof param2 === 'object' && param2 !== null && !Array.isArray(param2)) {
    details = { ...param2 };
    entityId = details.entityId || details.servidorId || details.id || details.path || details.pagina || 'N/A';
    entityType = details.entityType || details.tipoEntidade || 'SISTEMA';
  } else {
    entityId = param2 || 'N/A';
    entityType = param3 || 'SISTEMA';
    details = param4 || {};
  }

  return {
    details,
    entityId: String(entityId || 'N/A'),
    entityType: String(entityType || 'SISTEMA'),
  };
};

const inferActionType = (action, details = {}) => {
  if (details.tipoAcao) return details.tipoAcao;
  const firstPart = String(action || '').toUpperCase().split('_')[0];
  return ACTION_TYPE_BY_PREFIX[firstPart] || 'registro';
};

const resolveUserDisplayName = (usuario) => (
  usuario?.displayName ||
  usuario?.nome ||
  usuario?.name ||
  usuario?.nomeCompleto ||
  usuario?.email ||
  'Servidor'
);

const resolveMunicipioNome = (usuario, vinculo, details) => (
  details?.municipioNome ||
  usuario?.municipioNome ||
  vinculo?.municipioNome ||
  vinculo?.municipio_nome ||
  vinculo?.municipio ||
  'N/A'
);

export const useAuditLog = () => {
  const authContext = useAuth();
  const contextUser = authContext?.user;
  const contextVinculo = authContext?.vinculoSelecionado;

  const logAction = useCallback(async (action, param2 = null, param3 = null, param4 = {}) => {
    if (!db || !firestorePathId) {
      return false;
    }

    const sessionUser = getSessionUser();
    const sessionVinculo = getSessionVinculo();

    const usuario = contextUser || sessionUser;
    const vinculo = contextVinculo || sessionVinculo;

    if (!usuario?.uid && !usuario?.id) {
      return false;
    }

    const { details, entityId, entityType } = normalizeLogParams(param2, param3, param4);
    const sanitizedDetails = sanitizeValue(details);
    const municipioId = sanitizedDetails.municipioId || usuario?.municipioId || vinculo?.municipioId || 'N/A';

    const auditLogsCollectionRef = collection(
      db,
      `artifacts/${firestorePathId}/public/data/auditLogs`
    );

    const payload = {
      action: String(action || 'ACAO_NAO_INFORMADA'),
      entityId,
      entityType,
      userEmail: usuario?.email || 'servidor@portal.local',
      userId: usuario?.uid || usuario?.id,
      userName: resolveUserDisplayName(usuario),
      usuarioUid: usuario?.uid || usuario?.id,
      usuarioNome: resolveUserDisplayName(usuario),
      usuarioEmail: usuario?.email || null,
      timestamp: serverTimestamp(),
      details: safeJsonStringify({
        ...sanitizedDetails,
        lastAction: getLastUserAction(),
      }),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A',
      executedBy: PORTAL_ORIGIN,
      origem: PORTAL_ORIGIN,
      portal: PORTAL_ID,
      modulo: sanitizedDetails.modulo || 'Portal do Servidor',
      pagina: sanitizedDetails.pagina || (typeof window !== 'undefined' ? window.location.pathname : 'N/A'),
      rota: sanitizedDetails.rota || (typeof window !== 'undefined' ? window.location.pathname : 'N/A'),
      tela: sanitizedDetails.tela || sanitizedDetails.pagina || 'Portal do Servidor',
      tipoAcao: inferActionType(action, sanitizedDetails),
      alterouDados: Boolean(sanitizedDetails.alterouDados),
      municipioId,
      municipioNome: resolveMunicipioNome(usuario, vinculo, sanitizedDetails),
      descricao: sanitizedDetails.descricao || 'Acao registrada no Portal do Servidor.',
      url: typeof window !== 'undefined' ? window.location.href : 'N/A',
      sessionId: getSystemLogSessionId(),
      version: SYSTEM_VERSION,
    };

    try {
      await addDoc(auditLogsCollectionRef, payload);
      return true;
    } catch (error) {
      if (isAlreadyPersistedAudit(error)) return true;

      logErrorToService('error', PORTAL_ID, error, usuario, 'AUDIT_LOG_WRITE_ERROR', null, {
        action: String(action || 'ACAO_NAO_INFORMADA'),
        entityType,
        entityId,
        municipioId,
      });
      return false;
    }
  }, [contextUser, contextVinculo]);

  return { logAction };
};

export default useAuditLog;
